/** The topics the bank is organised by, in the order a picker should list them. */
export const BANK_TOPICS = ['entra-iam', 'sentinel', 'incident-response', 'general'];
/** Human labels. The slugs are storage; these are what a student reads. */
export const BANK_TOPIC_LABELS = {
    'entra-iam': 'Entra ID & identity',
    sentinel: 'Microsoft Sentinel',
    'incident-response': 'Incident response',
    general: 'General security',
};
/**
 * Roles students actually interview for, and the topic mix a panel for each would draw from.
 *
 * These are weights, not quotas. When a topic runs out of unseen questions the remainder comes
 * from the next-heaviest — a short session is worse than an unbalanced one, because the point
 * is rehearsal under length.
 */
export const BANK_POSITIONS = [
    {
        id: 'cloud-security-engineer',
        label: 'Cloud Security Engineer',
        mix: { 'entra-iam': 0.4, sentinel: 0.25, 'incident-response': 0.2, general: 0.15 },
    },
    {
        id: 'soc-analyst',
        label: 'SOC Analyst',
        mix: { sentinel: 0.4, 'incident-response': 0.35, general: 0.15, 'entra-iam': 0.1 },
    },
    {
        id: 'iam-engineer',
        label: 'IAM Engineer',
        mix: { 'entra-iam': 0.7, general: 0.2, 'incident-response': 0.1 },
    },
];
/** Session lengths offered. Five is a warm-up; twelve is a real loop. */
export const BANK_SESSION_LENGTHS = [5, 8, 12];
/**
 * At least one behavioural question in every session.
 *
 * Candidates neglect the behavioural half and interviewers do not, so a session that happens to
 * draw eight technical questions has rehearsed the easy half. One is a floor, not a target — the
 * mix below aims higher and this only catches the unlucky draw.
 */
export const MIN_BEHAVIOURAL = 1;
/** Roughly what a real panel spends its time on. */
const KIND_MIX = {
    technical: 0.5,
    scenario: 0.25,
    behavioural: 0.25,
};
/** Deterministic shuffle, so a seeded session can be reproduced when debugging a complaint. */
function shuffle(items, seed) {
    const out = items.slice();
    let s = seed || 1;
    for (let i = out.length - 1; i > 0; i -= 1) {
        s = (s * 1103515245 + 12345) & 0x7fffffff;
        const j = s % (i + 1);
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}
/** How many questions each topic should contribute, given a weighting and a total. */
export function allocate(mix, total) {
    const entries = Object.entries(mix).filter(([, w]) => w > 0);
    const raw = entries.map(([topic, w]) => ({ topic, exact: w * total }));
    const out = {};
    let assigned = 0;
    for (const r of raw) {
        out[r.topic] = Math.floor(r.exact);
        assigned += out[r.topic];
    }
    // Hand out the remainder to the largest fractional parts, so 0.4/0.25/0.2/0.15 of 8
    // comes out 3/2/2/1 rather than losing two questions to flooring.
    const byFraction = raw
        .map((r) => ({ topic: r.topic, frac: r.exact - Math.floor(r.exact) }))
        .sort((a, b) => b.frac - a.frac);
    let i = 0;
    while (assigned < total && byFraction.length) {
        out[byFraction[i % byFraction.length].topic] += 1;
        assigned += 1;
        i += 1;
    }
    return out;
}
/**
 * Choose the running order for one session.
 *
 * Unseen questions are preferred but seen ones are not excluded: a bank of 106 with a student
 * who has drilled heavily would otherwise produce sessions that get shorter over time, and
 * silently returning four questions when eight were asked for is worse than a repeat.
 */
export function selectSessionQuestions(opts) {
    const { pool, mix, length, seed } = opts;
    const seen = opts.seenIds ?? new Set();
    const want = allocate(mix, length);
    const rank = (q) => (seen.has(q.id) ? 1 : 0);
    const byTopic = new Map();
    for (const q of pool) {
        if (!byTopic.has(q.topic))
            byTopic.set(q.topic, []);
        byTopic.get(q.topic).push(q);
    }
    for (const [topic, list] of byTopic) {
        byTopic.set(topic, shuffle(list, seed + topic.length).sort((a, b) => rank(a) - rank(b)));
    }
    const picked = [];
    const takenIds = new Set();
    const kindCount = { technical: 0, scenario: 0, behavioural: 0 };
    const take = (topic, n) => {
        const list = byTopic.get(topic) ?? [];
        for (const q of list) {
            if (picked.length >= length || n <= 0)
                break;
            if (takenIds.has(q.id))
                continue;
            // Keep the kind mix honest while the topic still has alternatives to offer.
            const cap = Math.ceil(KIND_MIX[q.kind] * length) + 1;
            if (kindCount[q.kind] >= cap && list.some((o) => !takenIds.has(o.id) && kindCount[o.kind] < cap))
                continue;
            picked.push(q);
            takenIds.add(q.id);
            kindCount[q.kind] += 1;
            n -= 1;
        }
    };
    for (const [topic, n] of Object.entries(want))
        take(topic, n);
    // Backfill from the heaviest topics when one ran dry, then from anywhere.
    const heaviest = Object.entries(mix).sort((a, b) => b[1] - a[1]).map(([t]) => t);
    for (const topic of heaviest) {
        if (picked.length >= length)
            break;
        take(topic, length - picked.length);
    }
    if (picked.length < length) {
        for (const q of shuffle(pool, seed).sort((a, b) => rank(a) - rank(b))) {
            if (picked.length >= length)
                break;
            if (!takenIds.has(q.id)) {
                picked.push(q);
                takenIds.add(q.id);
                kindCount[q.kind] += 1;
            }
        }
    }
    // Guarantee the behavioural floor by swapping out a surplus technical question rather than
    // extending the session — the student asked for eight questions, not nine.
    if (kindCount.behavioural < MIN_BEHAVIOURAL) {
        const candidate = shuffle(pool, seed + 7)
            .sort((a, b) => rank(a) - rank(b))
            .find((q) => q.kind === 'behavioural' && !takenIds.has(q.id));
        const dropIndex = picked.map((q, i) => ({ q, i })).reverse().find(({ q }) => q.kind === 'technical')?.i;
        if (candidate && dropIndex !== undefined)
            picked[dropIndex] = candidate;
    }
    // Open on something answerable and close on something that makes them think, the way a real
    // interview is paced — not because the order changes the marks, but because it changes nerves.
    return shuffle(picked, seed + 3);
}
//# sourceMappingURL=bank.js.map